import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, MapPin, Clock, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TripHistory = () => {
  const navigate = useNavigate();
  const [dailySummary, setDailySummary] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/mobileapp/login'); return; }
    fetchDailySummary();
  }, []);

  const fetchDailySummary = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/driver/daily-summary`, { headers: { Authorization: `Bearer ${token}` } });
      setDailySummary(res.data);
    } catch { toast.error('Failed to fetch trip history'); }
    finally { setLoading(false); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  const formatTime = (t) => t ? new Date(t).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A';

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center space-x-3">
          <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate('/mobileapp/dashboard')}><ArrowLeft className="w-4 h-4" /></Button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Trip History</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? <div className="text-center py-12 text-slate-600">Loading...</div> :
         dailySummary.length === 0 ? (
          <Card className="border-slate-100"><CardContent className="py-12 text-center"><MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-600">No trips yet</p></CardContent></Card>
        ) : (
          <div className="space-y-6">
            {dailySummary.map((day) => (
              <Card key={day.date} data-testid={`day-${day.date}`} className="border-slate-100 shadow-sm">
                <CardHeader className="bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2"><Calendar className="w-5 h-5 text-slate-600" /><CardTitle className="text-lg">{formatDate(day.date)}</CardTitle></div>
                    <div className="flex items-center space-x-3">
                      <div className="text-right"><p className="text-xs text-slate-500">Total KM</p><p className="text-2xl font-bold text-emerald-600" data-testid={`total-km-${day.date}`}>{day.total_km.toFixed(1)}</p></div>
                      <div className="text-right"><p className="text-xs text-slate-500">Shifts</p><p className="text-2xl font-bold text-slate-900">{day.shift_count}</p></div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {day.shifts.map((shift, idx) => (
                    <div key={shift.id} data-testid={`shift-${shift.id}`} className="bg-white border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center"><span className="text-orange-600 font-bold text-sm">#{idx+1}</span></div>
                          <span className="font-medium text-slate-700">Shift {idx+1}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-emerald-600"><TrendingUp className="w-4 h-4" /><span className="font-bold text-lg">{shift.total_km?.toFixed(1)} km</span></div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><p className="text-xs text-slate-500 mb-1">Start</p><div className="flex items-center space-x-1"><Clock className="w-3 h-3 text-slate-400" /><p className="text-sm font-medium">{formatTime(shift.start_time)}</p></div></div>
                        <div><p className="text-xs text-slate-500 mb-1">End</p><div className="flex items-center space-x-1"><Clock className="w-3 h-3 text-slate-400" /><p className="text-sm font-medium">{formatTime(shift.end_time)}</p></div></div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-slate-100">
                        <div><p className="text-xs text-slate-500">Start KM</p><p className="text-base font-bold">{shift.start_km}</p></div>
                        <div><p className="text-xs text-slate-500">End KM</p><p className="text-base font-bold">{shift.end_km || 'N/A'}</p></div>
                        <div><p className="text-xs text-slate-500">Distance</p><p className="text-base font-bold text-emerald-600">{shift.total_km?.toFixed(1) || 'N/A'}</p></div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TripHistory;
